import { useCallback, useEffect, useRef, useState } from "react";

import type { BlsState } from "../domain/blsChannel";
import type { BlsEdge } from "../domain/blsMotion";
import { useBlsEdges } from "./useBlsEdges";

/** The slice of Web Audio the tones use, so tests can stand in for it. */
export type ToneAudioContext = Pick<
    AudioContext,
    "state" | "currentTime" | "destination" | "resume" | "close" | "createOscillator" | "createGain" | "createStereoPanner"
>;

/** Soft and short: a cue to each ear, not a beep to flinch at. */
const TONE_FREQUENCY_HZ = 440;
const TONE_SECONDS = 0.12;
const TONE_PEAK_GAIN = 0.3;

function playTone(context: ToneAudioContext, edge: BlsEdge) {
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const panner = context.createStereoPanner();

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(TONE_FREQUENCY_HZ, start);
    panner.pan.setValueAtTime(edge === "left" ? -1 : 1, start);
    // A short ramp in and out: a tone that starts or stops at full level clicks.
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(TONE_PEAK_GAIN, start + 0.01);
    gain.gain.linearRampToValueAtTime(0, start + TONE_SECONDS);

    oscillator.connect(gain).connect(panner).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + TONE_SECONDS + 0.02);
}

type Options = {
    state: BlsState | null;
    cyclesNow: (nowEpochMs?: number) => number;
    /**
     * Off on the therapist's screen: a tone from their speakers would reach
     * their microphone and arrive at the patient a second time, in mono.
     */
    enabled: boolean;
    createAudioContext?: () => ToneAudioContext;
};

export type BlsTones = {
    /** The browser is holding audio until somebody presses something. */
    soundBlocked: boolean;
    enableSound: () => Promise<void>;
};

/**
 * Alternating tones at the edges, generated on this device.
 *
 * Generated rather than streamed, because a published track is mono and the
 * whole point is which ear hears it.
 */
export function useBlsTones({
    state,
    cyclesNow,
    enabled,
    createAudioContext = () => new AudioContext(),
}: Options): BlsTones {
    const [soundBlocked, setSoundBlocked] = useState(false);
    const contextRef = useRef<ToneAudioContext | null>(null);
    const factoryRef = useRef(createAudioContext);
    useEffect(() => {
        factoryRef.current = createAudioContext;
    }, [createAudioContext]);

    const active = enabled && state !== null && state.running && state.settings.mode !== "visual";

    useEffect(() => {
        if (!active) return;
        if (!contextRef.current) contextRef.current = factoryRef.current();
        setSoundBlocked(contextRef.current.state === "suspended");
    }, [active]);

    const onEdge = useCallback((edge: BlsEdge) => {
        const context = contextRef.current;
        if (context && context.state === "running") playTone(context, edge);
    }, []);
    useBlsEdges({ state, cyclesNow, active, onEdge });

    useEffect(
        () => () => {
            void contextRef.current?.close();
            contextRef.current = null;
        },
        []
    );

    const enableSound = useCallback(async () => {
        const context = contextRef.current;
        if (!context) return;
        await context.resume();
        setSoundBlocked(context.state === "suspended");
    }, []);

    return { soundBlocked, enableSound };
}
